import type { MaybeRefOrGetter } from '@vueuse/shared'

import type { ICommunity } from '~/entities/container/community/interfaces/community'

import type { IContent } from '~/features-shared/content/interfaces/content'

import type { BasicContentPDValues, ContainerServiceRequest, ContentServiceRequest } from '~/features-generic/tracker/interfaces/tracker'

/**
 * Get basic content pd values for the provided content.
 * Feel free to override returns attributes depending on your needs.
 * @param content - Content object.
 * @returns Basic content tracker pd values.
 * @example
 * trackPageEvent({
 *   category: 'ContentMenu',
 *   label: 'showHistoryItem',
 *   event: 'click',
 *   pd: {
 *     component: 'useContentMenu',
 *     ...getBasicContentPdValues(content)
 *     context: 'RITUALS'
 *   }
 * })
 */
export function getBasicContentPdValues(
  content: MaybeRefOrGetter<IContent>,
): BasicContentPDValues {
  const _content = toValue(content)

  return {
    contentType: _content.model,
    contentURI: _content.subject_uri,
    containerType: _content.community_type !== 'dm' ? 'community' : 'conversation',
    containerURI: _content.community_subject_uri,
  }
}

/**
 * Get content service request pd values for the provided content.
 * Feel free to override returns attributes depending on your needs.
 * @param content - Content object.
 * @returns Content service request tracker pd values.
 * @example
 * await revertWiki({
 *   subjectUri: wiki.value.subject_uri,
 *   edition: editionIndex,
 * }, {
 *   category: 'WikiHistory',
 *   label: 'revertWikiRequest',
 *   pd: {
 *     crud: 'update',
 *     ...getContentServiceRequestPdValues(wiki)
 *   },
 * })
 */
export function getContentServiceRequestPdValues(
  content: MaybeRefOrGetter<IContent>,
): Omit<ContentServiceRequest, 'crud'> {
  const _content = toValue(content)

  return {
    contentType: _content.model,
    contentURI: _content.subject_uri,
    containerType: _content.community_type !== 'dm' ? 'community' : 'conversation',
    containerURI: _content.community_subject_uri,
  }
}

/**
 * Get container service request pd values for the provided container.
 * Feel free to override returns attributes depending on your needs.
 * @param container - Container object.
 * @returns Container service request tracker pd values.
 * @example
 * await getWikiTree({
 *   communityUri: community.value.subject_uri
 *   }, {
 *   category: 'WikiTree',
 *   label: 'getWikiTree',
 *   pd: {
 *     crud: 'read',
 *     ...getContainerServiceRequestPdValues(community)
 *   },
 * })
 */
export function getContainerServiceRequestPdValues(
  container: MaybeRefOrGetter<ICommunity>,
): Omit<ContainerServiceRequest, 'crud'> {
  const _container = toValue(container)

  return {
    containerType: _container.community_type !== 'dm' ? 'community' : 'conversation',
    containerURI: _container.subject_uri,
  }
}
